import {
    Button,
    Modal,
} from "@mrflark/ui";
import {
    ChevronDown,
    ChevronUp,
    PanelLeft,
    PanelRight,
} from "lucide-preact";
import {
    useEffect,
    useRef,
    useState,
} from "preact/compat";

import {
    splClient,
} from "../api/client.ts";
import {
    KeyboardShortcutsModal,
} from "../components/common/KeyboardShortcutsModal.tsx";
import {
    AiAnalysisModal,
} from "../components/media/AiAnalysisModal.tsx";
import {
    CropModal,
} from "../components/media/CropModal.tsx";
import {
    MediaViewer,
} from "../components/media/MediaViewer.tsx";
import type {
    MediaViewerRef,
} from "../components/media/MediaViewer.tsx";
import {
    AudioClassificationStrip,
    FilmstripNav,
    MusicIdentificationPanel,
    ShareModal,
    SimilarItemsModal,
    SimilarItemsPanel,
    ViewerActionBar,
    ViewerHeader,
    ViewerLeftSidebar,
    ViewerRightSidebar,
} from "../components/viewer/index.ts";
import {
    useKeyboardShortcuts,
    useViewerFilters,
    useViewerMutations,
    useViewerNavigation,
} from "../hooks/index.ts";
import {
    useAuthStore,
} from "../stores/authStore.ts";
import {
    useViewerStore,
} from "../stores/viewerStore.ts";

export default function ViewerPage() {
    const tagInputRef = useRef<HTMLInputElement>(null);
    const mediaViewerRef = useRef<MediaViewerRef>(null);
    const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
    const [showShortcutsModal, setShowShortcutsModal] = useState(false);
    const [showOcrOverlay, setShowOcrOverlay] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showAiAnalysis, setShowAiAnalysis] = useState(false);
    const [showSimilarModal, setShowSimilarModal] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    const [voteRefreshKey, setVoteRefreshKey] = useState(0);

    const {
        isAdmin,
        userId,
    } = useAuthStore();

    const {
        currentItem,
        isLoading,
        error,
        rightSidebarOpen,
        currentUserVote,
        filmstripVisible,
        setRightSidebarOpen,
        setCurrentUserVote,
        setCurrentItem,
        setFilmstripVisible,
    } = useViewerStore();

    const {
        loadNextItem,
        loadPreviousItem,
        canGoPrevious,
        handleNavigateToItem,
    } = useViewerNavigation();

    const {
        handleAddTag,
        handleRemoveTag,
        handleVote,
        votePending,
        deleteMutation,
        acceptSuggestionMutation,
        rejectSuggestionMutation,
    } = useViewerMutations(() => {
        setShowDeleteConfirm(false);
        loadNextItem();
    });

    const {
        handleApplyFilters,
        handleBrowsingModeChange,
        handleSearch,
        handleFetchExactCount,
        handleTagStatsClick,
        handleClearSearchAndBrowse,
    } = useViewerFilters(loadNextItem);

    useEffect(() => {
        if (currentItem && userId) {
            splClient.getMyVote(currentItem.id).then((info) => {
                setCurrentUserVote(info.vote);
            }).catch(() => {/* ignore */});
        }
    }, [currentItem?.id, userId, setCurrentUserVote]);

    // refresh recommendations when user votes
    // initial value must be null to satisfy preact's MutableRef
    // overload — `undefined` isn't allowed for refs in preact/compat
    const previousVoteRef = useRef<typeof currentUserVote | null>(null);
    useEffect(() => {
        if (
            previousVoteRef.current !== undefined &&
            previousVoteRef.current !== currentUserVote
        ) {
            setVoteRefreshKey((prev) => prev + 1);
        }
        previousVoteRef.current = currentUserVote;
    }, [currentUserVote]);

    useKeyboardShortcuts({
        disabled: showCropModal,
        onNext: loadNextItem,
        onPrevious: loadPreviousItem,
        onVote: () => currentItem && handleVote("like"),
        onFocusTagInput: () => tagInputRef.current?.focus(),
        onToggleFilmstrip: () => setFilmstripVisible(!filmstripVisible),
        onEscape: () => {
            tagInputRef.current?.blur();
            setShowOcrOverlay(false);
            setShowAiAnalysis(false);
            setShowSimilarModal(false);
            setShowCropModal(false);
            setShowShareModal(false);
        },
        onShowHelp: () => setShowShortcutsModal(true),
        onToggleFullscreen: () => mediaViewerRef.current?.toggleFullscreen(),
        onToggleOcrOverlay: () =>
            currentItem?.ocr_text && setShowOcrOverlay((prev) => !prev),
        onToggleAiAnalysis: () =>
            currentItem?.ai_analyses &&
            Object.keys(currentItem.ai_analyses).length > 0 &&
            setShowAiAnalysis((prev) => !prev),
        onToggleLeftSidebar: () => setLeftSidebarOpen((prev) => !prev),
        onToggleRightSidebar: () => setRightSidebarOpen(!rightSidebarOpen),
        onCrop: () =>
            isAdmin && currentItem?.media_type === "image" &&
            setShowCropModal(true),
        onDownload: () => {
            if (!currentItem) return;
            const link = document.createElement("a");
            link.href = currentItem.media_url;
            link.download = "";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        onEdit: () => tagInputRef.current?.focus(),
        onShare: () => currentItem && setShowShareModal(true),
    });

    return (
        <div className="min-h-screen bg-bg-primary text-text-primary">
            <ViewerHeader
                onToggleLeftSidebar={() => setLeftSidebarOpen(!leftSidebarOpen)}
            />

            <div className="pt-14 flex">
                <ViewerLeftSidebar
                    open={leftSidebarOpen}
                    onApplyFilters={handleApplyFilters}
                    onBrowsingModeChange={handleBrowsingModeChange}
                    onSearch={handleSearch}
                    onClearSearchAndBrowse={handleClearSearchAndBrowse}
                    onTagStatsClick={handleTagStatsClick}
                    onFetchExactCount={handleFetchExactCount}
                />

                <main
                    className={`
                        flex-1 min-h-[calc(100vh-3.5rem)] flex flex-col items-center justify-center p-8
                        transition-all duration-300
                        ${leftSidebarOpen ? "ml-72" : "ml-0"}
                        ${rightSidebarOpen ? "mr-72" : "mr-0"}
                    `}
                >
                    {error && (
                        <div className="mb-4 p-4 bg-error/10 border border-error/30 rounded-lg text-error">
                            {error}
                        </div>
                    )}

                    <MediaViewer
                        ref={mediaViewerRef}
                        item={currentItem}
                        isLoading={isLoading}
                        showOcrOverlay={showOcrOverlay}
                        onToggleAiAnalysis={() =>
                            currentItem?.ai_analyses &&
                            Object.keys(currentItem.ai_analyses).length > 0 &&
                            setShowAiAnalysis((prev) => !prev)}
                    />

                    {
                        /* chevron is always visible so the operator can re-show
                        the strip after hiding. the strip itself opts out
                        when filmstripVisible is false. */
                    }
                    <button
                        type="button"
                        onClick={() => setFilmstripVisible(!filmstripVisible)}
                        title={filmstripVisible
                            ? "Hide recent items (H)"
                            : "Show recent items (H)"}
                        className="mt-3 flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:bg-bg-tertiary rounded transition-colors cursor-pointer"
                    >
                        {filmstripVisible
                            ? <ChevronUp size={14} />
                            : <ChevronDown size={14} />}
                        {filmstripVisible ? "Hide history" : "Show history"}
                    </button>
                    <FilmstripNav onNavigateToItem={handleNavigateToItem} />

                    <ViewerActionBar
                        onPrevious={loadPreviousItem}
                        onVote={handleVote}
                        onNext={loadNextItem}
                        onDelete={() => setShowDeleteConfirm(true)}
                        onShare={() => setShowShareModal(true)}
                        canGoPrevious={canGoPrevious()}
                        isLoading={isLoading}
                        hasItem={!!currentItem}
                        votePending={votePending}
                        deletePending={deleteMutation.isPending}
                        currentVote={currentUserVote}
                    />

                    <AudioClassificationStrip currentItem={currentItem} />

                    <MusicIdentificationPanel
                        currentItem={currentItem}
                        onNavigateToItem={handleNavigateToItem}
                    />

                    <SimilarItemsPanel
                        currentItem={currentItem}
                        onNavigateToItem={handleNavigateToItem}
                    />
                </main>

                <ViewerRightSidebar
                    open={rightSidebarOpen}
                    tagInputRef={tagInputRef}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    onShowShortcuts={() => setShowShortcutsModal(true)}
                    onToggleOcrOverlay={() =>
                        currentItem?.ocr_text &&
                        setShowOcrOverlay((prev) => !prev)}
                    onToggleAiAnalysis={() =>
                        currentItem?.ai_analyses &&
                        Object.keys(currentItem.ai_analyses).length > 0 &&
                        setShowAiAnalysis((prev) => !prev)}
                    onToggleFullscreen={() =>
                        mediaViewerRef.current?.toggleFullscreen()}
                    onAcceptSuggestion={(tag) =>
                        acceptSuggestionMutation.mutate(tag)}
                    onRejectSuggestion={(tag) =>
                        rejectSuggestionMutation.mutate(tag)}
                    onShowSimilar={() => setShowSimilarModal(true)}
                    onCrop={() => setShowCropModal(true)}
                    showOcrOverlay={showOcrOverlay}
                    voteRefreshKey={voteRefreshKey}
                    onNavigateToItem={handleNavigateToItem}
                />
            </div>

            {!leftSidebarOpen && (
                <button
                    type="button"
                    onClick={() => setLeftSidebarOpen(true)}
                    className="fixed left-4 top-20 p-2 bg-bg-secondary border border-border rounded-md text-text-secondary hover:text-text-primary z-30"
                >
                    <PanelLeft size={20} />
                </button>
            )}

            {!rightSidebarOpen && (
                <button
                    type="button"
                    onClick={() => setRightSidebarOpen(true)}
                    className="fixed right-4 top-20 p-2 bg-bg-secondary border border-border rounded-md text-text-secondary hover:text-text-primary z-30"
                >
                    <PanelRight size={20} />
                </button>
            )}

            <KeyboardShortcutsModal
                isOpen={showShortcutsModal}
                onClose={() => setShowShortcutsModal(false)}
            />

            <AiAnalysisModal
                isOpen={showAiAnalysis}
                onClose={() => setShowAiAnalysis(false)}
                item={currentItem}
                onAcceptSuggestion={(tag) =>
                    acceptSuggestionMutation.mutate(tag)}
                onRejectSuggestion={(tag) =>
                    rejectSuggestionMutation.mutate(tag)}
            />

            <SimilarItemsModal
                isOpen={showSimilarModal}
                onClose={() => setShowSimilarModal(false)}
                item={currentItem}
                onNavigateToItem={handleNavigateToItem}
            />

            <CropModal
                isOpen={showCropModal}
                onClose={() => setShowCropModal(false)}
                item={currentItem}
                onCropComplete={() => {
                    if (!currentItem) return;
                    splClient.getMediaItem(currentItem.id).then(
                        (updatedItem) => {
                            const cacheBusted = {
                                ...updatedItem,
                                media_url: `${updatedItem.media_url}${
                                    updatedItem.media_url.includes("?")
                                        ? "&"
                                        : "?"
                                }t=${Date.now()}`,
                                thumbnail_url: updatedItem.thumbnail_url
                                    ? `${updatedItem.thumbnail_url}${
                                        updatedItem.thumbnail_url.includes("?")
                                            ? "&"
                                            : "?"
                                    }t=${Date.now()}`
                                    : undefined,
                            };
                            setCurrentItem(cacheBusted);
                        },
                    ).catch(() => {/* ignore */});
                }}
            />

            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                mediaId={currentItem?.id ?? null}
            />

            <Modal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Delete Item?"
                size="md"
            >
                <p className="text-text-secondary mb-4">
                    Are you sure you want to delete this item? This action will
                    soft-delete the item.
                </p>
                {currentItem && (
                    <div className="mb-4 flex justify-center">
                        {currentItem.media_type === "video"
                            ? (
                                <video
                                    src={currentItem.media_url}
                                    className="max-h-32 rounded-lg"
                                    muted
                                    autoPlay
                                    loop
                                />
                            )
                            : (
                                <img
                                    src={currentItem.media_url}
                                    alt="Item to delete"
                                    className="max-h-32 rounded-lg"
                                />
                            )}
                    </div>
                )}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={deleteMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="error"
                        onClick={() => deleteMutation.mutate()}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                    </Button>
                </div>
            </Modal>
        </div>
    );
}
